import {
  ProfessionalApplicationParameters,
  ProfessionalExperienceHopeEnum,
} from '@platform/client/schemas';
import { FormsMultilineInput, TagSelect } from '@platform/components';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../Application';
import { useApplicationLocalStorage } from '../application-hook';
import { ApplicationBackground } from '../ApplicationBackground';
import { ApplicationPageContent } from '../components/ApplicationPageContent';
import BackgroundImage from '../images/Updatedcomposite3.png';

type FormValues = {
  experienceHope: ProfessionalExperienceHopeEnum[];
  otherHope: string;
};

const HOPE_OPTIONS = [
  { value: 'learn-social-impact', label: 'Learn about social impact' },
  { value: 'use-improve-skills', label: 'Use and improve my skills' },
  { value: 'networking', label: 'Grow my network' },
  { value: 'help-community', label: 'Give back to my community' },
  { value: 'leadership', label: 'Develop leadership skills' },
  { value: 'other', label: 'Other' },
];

export const ExperienceHopeSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<ProfessionalApplicationParameters>();

  //Default open if the user has previously selected 'other'
  const [showTextbox, setShowTextbox] = useState(
    getApplicationDataByKey('experienceHope')?.includes('other') || false,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    watch,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      experienceHope: getApplicationDataByKey('experienceHope'),
      otherHope: getApplicationDataByKey('otherHope'),
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      experienceHope: data.experienceHope,
      experienceHopeOther: data.otherHope ? data.otherHope : null,
    });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      experienceHope: watch('experienceHope'),
      experienceHopeOther: watch('otherHope'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='What are you hoping to get out of this experience?'
        description="Select all that apply - let us know if there are any that we've missed."
      >
        <TagSelect
          tags={HOPE_OPTIONS}
          selectedTags={watch('experienceHope') || []}
          register={() =>
            register('experienceHope', {
              required: "Select at least one thing you're hoping to get out of this experience",
            })
          }
          error={errors.experienceHope?.message}
          onSelectionChange={(value: string[]) => {
            setValue('experienceHope', value as ProfessionalExperienceHopeEnum[]);
            if (value.includes('other')) {
              setShowTextbox(true);
            } else {
              setShowTextbox(false);
            }
          }}
        />
        {showTextbox && (
          <FormsMultilineInput
            placeholder='Type your answer here...'
            label='Tell us more about what you’re hoping to get out of this experience (optional)'
            characterCount={500}
            formsError={errors.otherHope}
            rules={{ maxLength: 500 }}
            control={control}
            name='otherHope'
          />
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
